import { useCallback, useEffect, useRef, useState } from 'react';
import { IconCrop, IconPhotoPlus, IconTrash } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../../i18n/hooks/useTranslation';
import { DEFAULT_ATS_PHOTO_CROP, loadAtsPhotoSource } from '../engines/atsCvPhotoCrop';
import AtsCvPhotoCropModal from './AtsCvPhotoCropModal';

/** A CV photo is a thumbnail on a page — more than this is wasted bytes. */
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * The optional portrait, and the framing of it.
 *
 * Three things are kept, not one: the picture as it will print (`photo`), the
 * picture as it was picked (`photoSource`), and the framing between them
 * (`photoCrop`). The source is what makes "adjust" possible at all — without it
 * the only thing left to re-crop is an already-cropped square, and every visit
 * to the dialog would cut a little more off.
 *
 * Both are downscaled on the way in: the picture rides inside the form, which
 * is what gets saved as a draft and embedded in the exported PDF, and a 4MB
 * phone photo would bloat both for something that prints at 24mm.
 */
export default function AtsCvPhotoField({ value, source, crop, onChange, disabled = false }) {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [editing, setEditing] = useState(null);
    const [dropping, setDropping] = useState(false);
    // A drag over a child fires `dragleave` on the parent, so the highlight has
    // to count enters and leaves rather than trust a single leave.
    const dragDepth = useRef(0);

    const handleFile = useCallback(async (file) => {
        // A locked field still sits in the page, and a drop or a paste reaches
        // it without going near the button the lock covers.
        if (!file || disabled) return;
        if (!file.type?.startsWith('image/')) {
            notifications.show({
                title: t('common.error'),
                message: t('atsCvMaker.photo.invalidType'),
                color: 'red',
            });
            return;
        }
        if (file.size > MAX_BYTES) {
            notifications.show({
                title: t('common.error'),
                message: t('atsCvMaker.photo.tooLarge'),
                color: 'red',
            });
            return;
        }
        try {
            // Straight into the dialog: the framing is the decision being made,
            // and asking for it now is cheaper than letting a bad crop reach
            // the preview and be noticed there.
            // No crop: a picture nobody has framed yet gets the dialog's own
            // opening framing, which puts the top of a portrait in the box.
            // The shape is carried over, though — somebody who chose passport
            // for the last photo means it for this one.
            setEditing({
                source: await loadAtsPhotoSource(file),
                crop: null,
                aspect: crop?.aspect || DEFAULT_ATS_PHOTO_CROP.aspect,
            });
        } catch {
            notifications.show({
                title: t('common.error'),
                message: t('atsCvMaker.photo.failed'),
                color: 'red',
            });
        }
    }, [crop?.aspect, disabled, t]);

    /* ------------------------------------------------- drop, and paste */

    const onDragEnter = (e) => {
        if (disabled || !e.dataTransfer?.types?.includes('Files')) return;
        e.preventDefault();
        dragDepth.current += 1;
        setDropping(true);
    };

    const onDragLeave = () => {
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (!dragDepth.current) setDropping(false);
    };

    const onDragOver = (e) => {
        if (disabled || !e.dataTransfer?.types?.includes('Files')) return;
        // Without this the browser takes the drop itself and navigates away
        // from the half-filled form to the dropped file.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    const onDrop = (e) => {
        if (disabled) return;
        e.preventDefault();
        dragDepth.current = 0;
        setDropping(false);
        const file = [...(e.dataTransfer?.files || [])][0];
        if (file) handleFile(file);
    };

    /**
     * Ctrl+V anywhere on the page, as long as the clipboard holds a picture.
     *
     * Deliberately not scoped to this field: a screenshot is taken and pasted
     * without a thought about where the caret is, and asking the user to click
     * the photo box first would be a rule nobody reads. Text pastes carry no
     * image and fall straight through to whatever input has focus, so ordinary
     * typing in the form is untouched.
     */
    useEffect(() => {
        const onPaste = (e) => {
            // While the crop dialog is open the picture is already chosen; a
            // paste then would throw away the framing being worked on.
            if (editing || disabled) return;
            const items = [...(e.clipboardData?.items || [])];
            const entry = items.find((i) => i.kind === 'file' && i.type.startsWith('image/'));
            const file = entry?.getAsFile() || [...(e.clipboardData?.files || [])]
                .find((f) => f.type?.startsWith('image/'));
            if (!file) return;
            e.preventDefault();
            handleFile(file);
        };
        document.addEventListener('paste', onPaste);
        return () => document.removeEventListener('paste', onPaste);
    }, [editing, disabled, handleFile]);

    // A photo imported from a PDF has no source of its own; it is its own
    // source, and re-cropping it simply works on what there is.
    const adjust = () => {
        const from = source || value;
        if (!from || disabled) return;
        setEditing({
            source: from,
            crop: crop ? { ...DEFAULT_ATS_PHOTO_CROP, ...crop } : null,
            aspect: crop?.aspect || DEFAULT_ATS_PHOTO_CROP.aspect,
        });
    };

    const applyCrop = ({ dataUrl, crop: nextCrop }) => {
        onChange({ photo: dataUrl, photoSource: editing.source, photoCrop: nextCrop });
        setEditing(null);
    };

    const buttonClass = 'inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2.5 text-[12px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200';

    return (
        <div>
            <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
            />
            {/* One bordered block rather than a bare row, so the picture and
                its buttons read as a single field. */}
            <div
                onDragEnter={onDragEnter}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={`flex items-center gap-3 rounded-xl border p-2.5 transition-colors ${
                    dropping
                        ? 'border-primary border-dashed bg-primary/5'
                        : 'border-[#ECE8E4] bg-[#FDFCFB] dark:border-zinc-700 dark:bg-zinc-900/40'
                }`}
            >
                {value ? (
                    <button
                        type="button"
                        onClick={adjust}
                        title={t('atsCvMaker.photo.adjust')}
                        className="flex-none overflow-hidden rounded-lg border border-[#ECE8E4] transition-colors hover:border-primary dark:border-zinc-700"
                    >
                        <img
                            src={value}
                            alt=""
                            className="block h-[56px] w-[56px] object-contain"
                        />
                    </button>
                ) : (
                    <div className="flex h-[56px] w-[56px] flex-none items-center justify-center rounded-lg border border-dashed border-[#D9D4CE] bg-white text-[#A8A099] dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-500">
                        <IconPhotoPlus size={20} stroke={1.7} />
                    </div>
                )}
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap gap-1.5">
                        {value ? (
                            <button type="button" onClick={adjust} className={buttonClass}>
                                <IconCrop size={14} stroke={1.8} />
                                {t('atsCvMaker.photo.adjust')}
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            className={buttonClass}
                        >
                            <IconPhotoPlus size={14} stroke={1.8} />
                            {value ? t('atsCvMaker.photo.replace') : t('atsCvMaker.photo.upload')}
                        </button>
                        {value ? (
                            <button
                                type="button"
                                onClick={() => onChange({ photo: '', photoSource: '', photoCrop: null })}
                                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2.5 text-[12px] font-semibold text-[#6B635C] transition-colors hover:border-[#D33A2C]/40 hover:text-[#D33A2C] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400"
                            >
                                <IconTrash size={14} stroke={1.8} />
                                {t('atsCvMaker.photo.remove')}
                            </button>
                        ) : null}
                    </div>
                    {disabled ? null : (
                        <p className="mt-1 text-[11px] leading-snug text-[#A8A099] dark:text-zinc-500">
                            {t('atsCvMaker.photo.dropHint')}
                        </p>
                    )}
                </div>
            </div>

            {editing ? (
                <AtsCvPhotoCropModal
                    source={editing.source}
                    crop={editing.crop}
                    aspect={editing.aspect}
                    onApply={applyCrop}
                    onClose={() => setEditing(null)}
                />
            ) : null}
        </div>
    );
}
